import React, { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import Authorize from '../../components/AuthComponent/Authorize';
import SaveButton from '../../components/Buttons/SaveButton';
import ButtonBar from '../../components/Fields/ButtonBar';
import Form from '../../components/Fields/Form';
import SelectListField from '../../components/Fields/SelectListField';
import TextboxField from '../../components/Fields/TextboxField';
import { FormConfig } from '../../components/Fields/Types';
import useSelectListOptions from '../../components/Hooks/SelectListOptionsHook/useSelectListOptions';
import useValidation from '../../components/Hooks/ValidationHook/useValidation';
import { HorizontalTabs } from '../../components/Tabs/HorizontalTabs';
import { ApplicationFunctions, useAuth } from '../../contexts/authContext';
import { useNotifications } from '../../contexts/notificationContext';
import RegionModel from '../../models/RegionModel';
import DataService from '../../services/data.service';
import IdParams from '../../types/IdParams';
import { Result } from '../../types/Result';
import Unauthorized from '../Authentication/Unauthorized';
import Region_tab_Countries from './Tabs/Region_tab_Countries';
import Region_tab_SubRegions from './Tabs/Region_tab_SubRegions';

const RegionView: React.FC = () => {
    const [serverErrors, setServerErrors] = useState<Result>({ isSuccesful: true, messages: [] });
    const { handleResult, handleException } = useNotifications();
    const [isAuthorized, setIsAuthorized] = useState<boolean>(true);

    const [formData, setFormData] = useState<RegionModel>({
        id: 0,
        name: '',
        parentRegionId: 0,
        concurrencyId: ''
    });

    const Regions = useSelectListOptions('api/v1/selectlist/RegionNameTrees', '-- Select region');

    let idParams: IdParams = useParams();
    let regionId: number | undefined;
    if (idParams.id && !Number.isNaN(parseInt(idParams.id))) {
        regionId = parseInt(idParams.id);
    }

    useEffect(() => {
        if (regionId !== undefined) {
            DataService.getById<RegionModel>('api/v1/regions/', regionId)
                .then(response => setFormData(response))
                .catch(e => {
                    if (e.response?.data?.status === 401)
                        setIsAuthorized(false);
                    else
                        handleException(e);
                });
        }
    }, [regionId]);

    function submit() {
        if (formData) {
            if (formData.id === 0)
                DataService
                    .create<RegionModel>(`api/v1/regions/`, formData)
                    .then(response => {
                        handleResult(response);
                        setServerErrors(response);
                        if (response.isSuccesful === true && response.data)
                            setFormData(response.data);
                    })
                    .catch(e => handleException(e));
            else
                DataService
                    .update<RegionModel>(`api/v1/regions/`, formData)
                    .then(response => {
                        handleResult(response);
                        setServerErrors(response);
                        if (response.isSuccesful === true && response.data)
                            setFormData(response.data);
                    })
                    .catch(e => handleException(e));
        }
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'name', label: 'Name', validationRules: [{ type: 'isRequired' }] },
            { name: 'parentRegionId', label: 'Parent region' },
        ],
        handleSubmit: submit,
        showErrors: 'always',
    };

    const { getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData, serverErrors);

    const { isAuthorizedFor } = useAuth();
    if (regionId === undefined) {
        if (!isAuthorizedFor(ApplicationFunctions.EditRegion)) {
            return (<Unauthorized />);
        }
    }
    else if (!isAuthorized) {
        return (<Unauthorized />);
    }

    return (
        <div className='main-content'>
            <Form title='Region' footer={(
                <Row>
                    <Col md='12'>
                        <ButtonBar offset={2}>
                            <Authorize applicationFunction={ApplicationFunctions.EditRegion}>
                                <SaveButton  {...getSubmitButtonProps()} />
                            </Authorize>
                        </ButtonBar>
                    </Col>
                </Row>
            )}>
                <Container fluid>
                    <Row>
                        <Col md='12'>
                            <TextboxField {...getFieldProps('name')} />
                            <SelectListField {...getFieldProps('parentRegionId')} options={Regions} />
                        </Col>
                    </Row>
                </Container>
            </Form>
            {regionId && (
                <Container fluid>
                    <Row>
                        <Col md='12'>
                            <HorizontalTabs
                                id='vtabs'
                                defaultActiveKey='subRegions'
                                tabs={[
                                    {
                                        title: 'Sub-regions',
                                        eventKey: 'subRegions',
                                        component: <Region_tab_SubRegions id={regionId} />,
                                    },
                                    {
                                        title: 'Countries',
                                        eventKey: 'countries',
                                        component: <Region_tab_Countries id={regionId} />,
                                    },
                                ]}
                            />
                        </Col>
                    </Row>
                </Container>
            )}
        </div>
    );
}
export default RegionView;